import React, { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { getAgentServices } from '../../../api/agents/agent-services';
import AgentServiceCard from './AgentServiceCard';
import ServiceDetails from '../../store/service-details/ServiceDetails';
import RightSidebar from '../../layout/RightSidebar';

import styles from '../../store/styles/ServicesList.module.css';

const gridVariants = {
    hidden: {},
    show: {
        transition: {
            staggerChildren: 0.06,
            delayChildren: 0.08,
        },
    },
};

const AgentServicesList = ({ agentId, type = 'all' }) => {
    const [services, setServices] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const observer = useRef();

    const [selectedServiceId, setSelectedServiceId] = useState(null);
    const selectedService = useMemo(() => 
        services?.find(service => service.id === selectedServiceId) || null,
        [services, selectedServiceId]
    );

    const sortedServices = useMemo(() => {
        return [...services].sort((a, b) => {
            if (a.isDefault === b.isDefault) return 0;
            return a.isDefault ? -1 : 1;
        });
    }, [services]);

    const lastServiceRef = useCallback(node => {
        if (isLoading) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prevPage => prevPage + 1);
            }
        });

        if (node) observer.current.observe(node);
    }, [isLoading, hasMore]);

    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true);
            const data = await getAgentServices(agentId, page, 24, type);
            setServices(prev => page === 1 ? data.services : [...prev, ...data.services]);
            setHasMore(data.hasMore);
        } catch (err) {
            setError('Failed to load agent services');
            console.error('Error fetching agent services:', err);
        } finally {
            setIsLoading(false);
        }
    }, [agentId, page, type]);

    useEffect(() => {
        // Reset state when agentId or type changes
        setServices([]);
        setPage(1);
        setHasMore(true);
        setError(null);
        setSelectedServiceId(null);
    }, [agentId, type]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleServiceClick = useCallback(service => {
        setSelectedServiceId(service.id);
    }, []);

    const handleCloseDetails = useCallback(() => {
        setSelectedServiceId(null);
    }, []);

    if (error) {
        return <div className={styles.error}>{error}</div>;
    }

    return (
        <>
            <div className={styles.container} style={{ padding: '0' }}>
                {sortedServices.length > 0 && (
                    <motion.div
                        className={styles.grid}
                        variants={gridVariants}
                        initial="hidden"
                        animate="show"
                    >
                        <AnimatePresence mode="popLayout">
                            {sortedServices.map((service, index) => (
                                <AgentServiceCard
                                    key={service.id}
                                    service={service}
                                    agentId={agentId}
                                    setServices={setServices}
                                    onClick={() => handleServiceClick(service)}
                                    type={type}
                                    ref={index === sortedServices.length - 1 ? lastServiceRef : null}
                                    isShowDelete={type !== 'owned'}
                                />
                            ))}
                        </AnimatePresence>
                    </motion.div>
                )}
                {isLoading && (
                    <div className={styles.text}>Loading services...</div>
                )}
                {services.length === 0 && !isLoading && (
                    <div className={styles.text}>
                        {type === 'added' ? 'No services added to this agent' : 'No services found'}
                    </div>
                )}
            </div>

            <RightSidebar
                isOpen={!!selectedServiceId}
                onClose={handleCloseDetails}
            >
                {selectedService && (
                    <ServiceDetails
                        service={selectedService}
                        isAdded={selectedService?.isAdded}
                        isEnabled={selectedService?.isEnabled}
                        setServices={setServices}
                        isAgentContext={true}
                        agentId={agentId}
                    />
                )}
            </RightSidebar>
        </>
    );
};

export default memo(AgentServicesList);
